/**
 * hero — общий балл качества с полосой и плашкой «требует внимания».
 * Зеркалит `HeroScore` (analysis-utils.tsx): те же данные (qualityScore,
 * attentionFlag/attentionReason), тот же цвет по scoreBand, но верстается
 * инлайн под классы `.hero`/`.hero-label`/`.hero-score`/`.flag` из shell.ts —
 * `.hero` не сведён к примитиву `card()`, у него собственная разметка.
 *
 * Без балла и вне категории `other` секция пуста (нечего показывать — анализ
 * ещё не оценил качество), кроме поднятого флага внимания: он печатается и
 * без балла — отдельной карточкой, как на экране (#2020). Для категории `other` (звонок или чат — оба
 * категоризуются одинаково) без балла печатается пояснение вместо hero
 * (зеркалит `OtherCategoryNotice` + условие `showOtherCategoryNotice` в
 * AnalysisPanel.tsx). Ветки взаимоисключающие: категория `other` даёт заметку
 * ТОЛЬКО пока баллов нет — кастомизированный профиль, вернувший балл для
 * `other`, получает обычный hero. Флаг без балла идёт после заметки категории
 * и до строки про веса — тем же порядком, что `AnalysisPanel`.
 *
 * Строка «балл в CRM и алертах посчитан с весами на момент анализа» (#874) —
 * как на карточке: под баллом, а без балла (текущие веса не дают звонку балла)
 * — отдельной заметкой.
 */
import { bar, safeNumber } from '../primitives';
import { scoreHex } from '../tokens';
import { escapeHtml } from '../escape';
import { formatQualityScore } from '../../format-quality-score';
import type { SectionFn } from '../types';

export const renderHero: SectionFn = (input, t) => {
  const { record, analysis } = input;
  // `qualityScore` — приведение типа, не гарантия (см. `safeNumber` в
  // primitives.ts); нечисловое значение трактуется так же, как отсутствие
  // балла, и уходит в ту же ветку ниже.
  const score = safeNumber(analysis.result.summary?.qualityScore);
  const weightsNote = analysis.scoredWithEarlierWeights
    ? `<p>${escapeHtml(t('records.analysisPanel.qualityScore.weightedAtAnalysis'))}</p>`
    : '';

  const { attentionFlag, attentionReason } = analysis.result.summary ?? {};
  const flag = attentionFlag
    ? `<div class="flag"><b>${escapeHtml(t('records.analysisPanel.attentionRequired'))}</b>` +
      `${attentionReason ? `<p>${escapeHtml(attentionReason)}</p>` : ''}</div>`
    : '';

  if (score == null) {
    // Чаты теперь категоризуются наравне со звонками (см. `showOtherCategoryNotice`
    // в AnalysisPanel.tsx), поэтому ограничение по типу записи снято здесь тоже.
    const isOther = record.callCategory === 'other';
    const otherNotice = isOther
      ? `<div class="note"><b>${escapeHtml(t('records.otherCategoryNotice.title'))}</b>` +
        `${escapeHtml(t('records.otherCategoryNotice.body'))}</div>`
      : '';
    // Флаг поднят и без балла (#2020): отдельная карточка, не часть hero с баллом.
    const flagCard = flag ? `<div class="hero">${flag}</div>` : '';
    const weights =
      !isOther && weightsNote ? `<div class="note">${weightsNote}</div>` : '';
    return `${otherNotice}${flagCard}${weights}`;
  }

  return (
    `<div class="hero"><div class="hero-label">${escapeHtml(t('records.analysisPanel.qualityScore.overallScore'))}</div>` +
    `<div class="hero-score" style="color:${scoreHex(score)}">${escapeHtml(formatQualityScore(score))}<small>/100</small></div>` +
    `${bar(score)}${weightsNote}${flag}</div>`
  );
};
